import React, { useState, useEffect } from 'react';
import { scannerAPI, adminAPI, authAPI, webhookAPI, securityPolicyAPI } from '../services/api';
import './ScannerConfigModal.css';

function ScannerConfigModal({ scanner, onClose, onUpdated }) {
    const [config, setConfig] = useState({
        functionalityType: scanner.config.functionalityType,
        webhookId: scanner.config.webhookId || null,
        securityPolicyId: scanner.config.securityPolicyId || null
    });

    const [userIds, setUserIds] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [webhooks, setWebhooks] = useState([]);
    const [securityPolicies, setSecurityPolicies] = useState([]);
    const [isAdmin, setIsAdmin] = useState(false);
    const [currentUserId, setCurrentUserId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    useEffect(() => {
        loadUserInfo();
        loadCustomers();
        loadCurrentAssociations();
        loadWebhooks();
        loadSecurityPolicies();
    }, []);

    const loadUserInfo = async () => {
        try {
            const response = await authAPI.getCurrentUser();
            const user = response.data.data.user;
            setIsAdmin(user.role === 'ADMIN');
            setCurrentUserId(user.id);
        } catch (err) {
            console.error('Failed to load user info:', err);
        }
    };

    const loadCustomers = async () => {
        try {
            const response = await adminAPI.getUsers({ role: 'CUSTOMER' });
            setCustomers(response.data.data.users);
        } catch (err) {
            console.error('Failed to load customers:', err);
        }
    };

    const loadCurrentAssociations = () => {
        if (scanner.associatedUsers) {
            setUserIds(scanner.associatedUsers.map(u => u.id));
        }
    };

    const loadWebhooks = async () => {
        try {
            const response = await webhookAPI.getWebhooks();
            let availableWebhooks = response.data.data.webhooks;

            // If scanner has an assigned webhook that user didn't create, include it
            if (scanner.config.webhook && scanner.config.webhookId) {
                const hasWebhook = availableWebhooks.some(w => w.id === scanner.config.webhookId);
                if (!hasWebhook) {
                    // Add the currently assigned webhook so it appears in the dropdown
                    availableWebhooks = [scanner.config.webhook, ...availableWebhooks];
                }
            }

            setWebhooks(availableWebhooks);
        } catch (err) {
            console.error('Failed to load webhooks:', err);
        }
    };

    const loadSecurityPolicies = async () => {
        try {
            const response = await securityPolicyAPI.getPolicies();
            let availablePolicies = response.data.data.policies;

            // If scanner has an assigned security policy that user didn't create, include it
            if (scanner.config.securityPolicy && scanner.config.securityPolicyId) {
                const hasPolicy = availablePolicies.some(p => p.id === scanner.config.securityPolicyId);
                if (!hasPolicy) {
                    // Add the currently assigned policy so it appears in the dropdown
                    availablePolicies = [scanner.config.securityPolicy, ...availablePolicies];
                }
            }

            setSecurityPolicies(availablePolicies);
        } catch (err) {
            console.error('Failed to load security policies:', err);
        }
    };

    const handleChange = (field, value) => {
        setConfig((prev) => ({ ...prev, [field]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');
        setLoading(true);

        try {
            // Update scanner config
            await scannerAPI.updateScannerConfig(scanner.id, config);

            // Update customer associations if admin
            if (isAdmin) {
                await adminAPI.setScannerUsers(scanner.id, userIds);
            }

            setSuccess('Configuration updated successfully');
            setTimeout(() => {
                onUpdated();
            }, 1000);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to update configuration');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>Configure Scanner: {scanner.name}</h2>
                    <button className="close-btn" onClick={onClose}>×</button>
                </div>

                <form onSubmit={handleSubmit}>
                    {error && <div className="error">{error}</div>}
                    {success && <div className="success">{success}</div>}

                    <div className="form-group">
                        <label>Functionality Type</label>
                        <select
                            value={config.functionalityType}
                            onChange={(e) => handleChange('functionalityType', e.target.value)}
                            disabled={loading}
                        >
                            <option value="MDB">MDB (Placeholder)</option>
                            <option value="WEBHOOK">Webhook</option>
                        </select>
                    </div>

                    {config.functionalityType === 'WEBHOOK' && (
                        <div className="form-group">
                            <label>Webhook Configuration</label>
                            <select
                                value={config.webhookId || ''}
                                onChange={(e) => handleChange('webhookId', e.target.value ? parseInt(e.target.value) : null)}
                                disabled={loading}
                            >
                                <option value="">None</option>
                                {webhooks.map(webhook => (
                                    <option key={webhook.id} value={webhook.id}>
                                        {webhook.name}
                                        {!isAdmin && webhook.userId !== currentUserId ? ' (assigned by another user)' : ''}
                                    </option>
                                ))}
                            </select>
                            <small>Select a webhook configuration for this scanner. You can see the currently assigned webhook even if created by another user.</small>
                        </div>
                    )}

                    <div className="form-group">
                        <label>Security Policy *</label>
                        <select
                            value={config.securityPolicyId || ''}
                            onChange={(e) => handleChange('securityPolicyId', e.target.value ? parseInt(e.target.value) : null)}
                            disabled={loading}
                        >
                            <option value="">Select a security policy...</option>
                            {securityPolicies.map(policy => (
                                <option key={policy.id} value={policy.id}>
                                    {policy.name}
                                    {!isAdmin && policy.userId !== currentUserId ? ' (assigned by another user)' : ''}
                                </option>
                            ))}
                        </select>
                        <small>Select a security policy to define validation rules. You can see the currently assigned policy even if created by another user.</small>
                    </div>

                    {isAdmin && (
                        <div className="form-group">
                            <label>Related Customers</label>
                            <div className="customer-list">
                                <div className="customer-item">
                                    <label>
                                        <input
                                            type="checkbox"
                                            checked={userIds.length === 0}
                                            onChange={() => setUserIds([])}
                                            disabled={loading}
                                        />
                                        <span>None</span>
                                    </label>
                                </div>
                                {customers.map(customer => (
                                    <div key={customer.id} className="customer-item">
                                        <label>
                                            <input
                                                type="checkbox"
                                                checked={userIds.includes(customer.id)}
                                                onChange={(e) => {
                                                    if (e.target.checked) {
                                                        setUserIds([...userIds, customer.id]);
                                                    } else {
                                                        setUserIds(userIds.filter(id => id !== customer.id));
                                                    }
                                                }}
                                                disabled={loading}
                                            />
                                            <span>{customer.name} ({customer.email})</span>
                                        </label>
                                    </div>
                                ))}
                            </div>
                            <small>Select customers who can access this scanner</small>
                        </div>
                    )}

                    <div className="modal-footer">
                        <button type="submit" disabled={loading}>
                            {loading ? 'Saving...' : 'Save Configuration'}
                        </button>
                        <button
                            type="button"
                            className="secondary"
                            onClick={onClose}
                            disabled={loading}
                        >
                            Cancel
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

export default ScannerConfigModal;
